import styles from "./ScheduleUserRow.module.scss"
import React from "react"
import { observer } from "mobx-react"
import { ScheduleUnitType, fields, ScheduleUnitSnapshotType } from "../models/schedule-unit"
import type { ScheduleStoreType } from "../models/schedule-store"
import { EmployerName } from "modules/common/components/employer/EmployerName"
import { Money } from "modules/common/components/money/Money"
import { Form, Formik, Field, FieldProps, FormikProps } from "formik"
import ScheduleSchema from "./validation"
import { Classes, Tooltip } from "@blueprintjs/core"
import { PlanrPopover } from "modules/common/components/planr/popover/Popover"
import { MODAL_AWAIT_DELAY } from "modules/common/services/form/textarea"
import { EmployerDictionaryItemType } from "modules/spending/employee/models/employee-dictionary"
import { BonusCorrection } from "./BonusCorrection"
import { VacationDetails } from "./VacationDetails"
import { MoneyInput } from "modules/common/components/money/MoneyInput"
import { InvisibleSubmit } from "modules/common/components/form"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { texts } from "modules/common/texts"
import { PlanrButton } from 'modules/common/components/planr/button/Button'
import { AdditionalSumsEditor } from './AdditionalSumsEditor'
import { GlobalEscape } from 'modules/common/components/hotkeys/GlobalEscape'

const schema = ScheduleSchema()

/**
 * Строка берёт свой узел из стора по `rowId`, а не получает его пропом.
 *
 * `load()` пересобирает `rows` через `applySnapshot`, а `id` в моделях — обычная
 * строка, не `types.identifier`, поэтому узлы строк каждый раз создаются заново.
 * Строка при этом остаётся той же (ключ — id плана) и получала бы новый узел
 * пропом. React 19 в dev разбирает сменившиеся пропсы вглубь, поднимает ленивое
 * поле `plan.employer` вне фазы инициализации, MST бросает проверку из фазы
 * коммита — и навигация замирает: адрес меняется, экран нет. Хватает двойного
 * `componentDidMount` страницы под StrictMode. См. рабочий элемент #27.
 */
class Row extends React.Component<ScheduleUserRowProps, ScheduleUserRowState> {
  formProps: FormikProps<any> | null = null;
  div = React.createRef<HTMLDivElement>();
  submit = React.createRef<HTMLButtonElement>();

  constructor(props: ScheduleUserRowProps) {
    super(props)
    this.state = {
      open: false,
      additionalSumsOpen: false
    }
  }
  componentDidMount() {
    this.focusInput()
  }

  componentDidUpdate(prevProps: ScheduleUserRowProps) {
    if (!prevProps.editMode && this.props.editMode) {
      this.focusInput()
    }
  }

  get schedule() {
    return this.props.store.rowByPlanId.get(this.props.rowId)
  }

  render() {
    const schedule = this.schedule
    if (!schedule) {
      return null
    }

    const { editMode, onSave, onSaved, bonusTaxeHidden, readonly } = this.props
    const user = schedule.user
    const correctionTooltipVisible = schedule.plan.bonusCorrection !== 0 && !editMode

    return (
      <div
        ref={this.div}
        className={`schedule-user-row ${styles.row} ${this.isHighlighted() ? styles.highlighted : ""} ${editMode ? styles.edited : ""
          }`}
        onDoubleClick={this.onDoubleClick}
        onClick={this.onClick}
        onContextMenu={this.onContextMenu}
      >
        {editMode && <GlobalEscape onEscape={this.cancelEdit} />}
        <Formik
          initialValues={getFormValues(schedule.plan)}
          enableReinitialize={true}
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting }) => {
            const succsessfull = await onSave(schedule.plan.id, values)
            if (succsessfull) {
              onSaved()
            } else {
              setSubmitting(false)
            }
          }}
        >
          {(formProps) => {
            this.formProps = formProps
            const baseGrade = user?.employerPositions?.[0]?.grades?.find(
              (g) => g.guid === user.employerPositions[0]?.gradeGuid
            )
            // ранг показываем «на тот период»: только если в этом месяце есть доплата за ранг,
            // с процентом именно этого месяца (история 8%/15% по месяцам)
            const grade =
              baseGrade && schedule.plan.bonusGradePercent > 0
                ? { guid: baseGrade.guid, gradeName: baseGrade.gradeName, percent: schedule.plan.bonusGradePercent }
                : null
            const addSums = (formProps.values as any)[fields.additionalSums]
            return (
              <Form>
                <div className={`schedule-user-name ${styles.cell}`}>
                  {user && !editMode ? <EmployerName name={schedule.periodName || user.name} position={user.employerPosition.name} grade={grade as any} /> : null}

                  {editMode && (
                    <>
                      <div className={`${styles.half} ${styles.actual}`}>&nbsp;</div>
                      <div className={styles.half}>
                        {/* сотрудник строки не меняется: состав штатки ведётся из карточки сотрудника */}
                        {user ? <EmployerName name={schedule.periodName || user.name} position={user.employerPosition.name} grade={null as any} /> : null}
                      </div>
                    </>
                  )}

                  {editMode && (
                    <div>
                      {this.formProps.dirty && <div className={styles.error}>Есть несохраненные данные!</div>}
                      {this.formProps.dirty && this.propagatesForward && (
                        <div className={styles.hint}>
                          Оклад, премия, вычет и доплаты применятся и к следующим месяцам — до месяца, где они уже
                          другие
                        </div>
                      )}
                      <div className="buttons">
                        <InvisibleSubmit innerRef={this.submit} />
                        <GeneralIcon
                          title={texts.save}
                          type="general-redo"
                          onClick={this.clickSubmit}
                          style={{
                            cursor: "pointer",
                            marginTop: "5px",
                            marginRight: "16px",
                            color: "#1DD278",
                          }}
                          className={this.formProps.dirty ? styles.blinking : ""}
                          size={20}

                        />
                        <GeneralIcon
                          type="general-edit"
                          onClick={() => {
                            this.setState({ open: true })
                          }}
                          title="Корр. премии"
                          style={{
                            cursor: "pointer",
                            marginTop: "5px",
                            marginRight: "16px",
                            color: "#4D9AD3",
                          }}
                          size={20}

                        />
{schedule.canRemove && (
                        <RemoveConfirmation onConfirmed={this.onRemove} what={this.removeLabel}>
                          {({ confirmRemoving }) => (
                            <GeneralIcon
                              title={texts.remove}
                              type="general-trash"
                              onClick={confirmRemoving}
                              style={{
                                cursor: "pointer",
                                marginTop: "5px",
                                color: "#E31818",
                              }}
                              size={20}

                            />
                          )}
                        </RemoveConfirmation>
                        )}
                      </div>
                    </div>

                  )}
                </div>
                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    <Money amount={schedule.actual.finalSalary} />
                  </div>
                  <div className={styles.half}>
                    <Money amount={schedule.plan.finalSalary} />
                  </div>
                </div>
                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    <Money amount={schedule.actual.totalSalary} />
                  </div>
                  <div className={styles.half}>
                    {!editMode && <Money amount={schedule.plan.totalSalary} />}
                    {editMode && (
                      <Field name={fields.salary}>
                        {({ field, form }: FieldProps) => {
                          return (
                            <MoneyInput
                              className="in-cell planr-tiny-input"
                              id={field.name}
                              small={true}
                              autoComplete="off"
                              data-lpignore="true"
                              value={field.value}
                              onMoneyChange={(money) => {
                                form.setFieldValue(field.name, money.value)
                                form.setFieldTouched(field.name)
                              }}
                            />
                          )
                        }}
                      </Field>
                    )}
                  </div>
                </div>
                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    {correctionTooltipVisible && (
                      <Tooltip
                        className={Classes.TOOLTIP_INDICATOR}
                        content={
                          <span>
                            {schedule.plan.bonusCorrectionComment || "Корректировка"}:
                            <br />
                            <Money amount={schedule.plan.bonusCorrection} />
                          </span>
                        }
                      >
                        <Money amount={schedule.actual.finalBonus} />
                      </Tooltip>
                    )}
                    {!correctionTooltipVisible && <Money amount={schedule.actual.finalBonus} />}
                  </div>

                  <div className={styles.half}>
                    {!editMode && (
                      <>
                        {schedule.plan.additionalSums && schedule.plan.additionalSums.length > 0 ? (
                          // Если есть доп. суммы - используем вертикальную структуру как у workedOutGrade
                          <div className={styles.bonusWorked}>
                            {/* Общая сумма (бонус + доп. суммы) */}
                            <div className={styles.total}>
                              <Money amount={schedule.plan.bonus + schedule.plan.addSums} />
                            </div>

                            {/* Бонус */}
                            <PlanrPopover
                              interactionKind="hover"
                              usePortal={true}
                              placement="left"
                              enforceFocus={false}
                              content={
                                <div className={styles.bonusPopup}>
                                  <div className={styles.popupRow}>
                                    <span>Премия:</span>
                                  </div>
                                  <div className={styles.popupRow}>
                                    <strong>
                                      <Money amount={schedule.plan.bonus} />
                                    </strong>
                                  </div>
                                </div>
                              }
                            >
                              <div className={styles.workedOut}>
                                <Money amount={schedule.plan.bonus} className="bp6-tooltip-indicator" />
                              </div>
                            </PlanrPopover>

                            {/* Дополнительные суммы */}
                            <PlanrPopover
                              interactionKind="hover"
                              usePortal={true}
                              placement="left"
                              enforceFocus={false}
                              content={
                                <div className={styles.bonusPopup}>
                                  {schedule.plan.additionalSums.map((sum, idx) => (
                                    <div key={sum.guid} className={styles.popupRow}>
                                      <span>{sum.name || 'Доп. выплата'}:</span>
                                      <strong>
                                        <Money amount={sum.sum} />
                                      </strong>
                                    </div>
                                  ))}
                                </div>
                              }
                            >
                              <div className={styles.grade}>
                                <Money amount={schedule.plan.additionalSums.reduce((total, sum) => total + sum.sum, 0)} className="bp6-tooltip-indicator" />
                              </div>
                            </PlanrPopover>
                          </div>
                        ) : (
                          // Только бонус - обычное отображение как в других столбцах
                          <Money amount={schedule.plan.bonus} />
                        )}
                      </>
                    )}

                    {editMode && (
                      <div className={styles.bonus}>
                        <Field name={fields.bonus}>
                          {({ field, form }: FieldProps) => {
                            return (
                              <MoneyInput
                                className="in-cell planr-tiny-input"
                                id={field.name}
                                small={true}
                                autoComplete="off"
                                data-lpignore="true"
                                value={field.value}
                                onMoneyChange={(money) => {
                                  form.setFieldValue(field.name, money.value)
                                  form.setFieldTouched(field.name)
                                }}
                              />
                            )
                          }}
                        </Field>

                        <BonusCorrection
                          open={this.state.open}
                          toggleOpen={() => {
                            this.setState({ open: !this.state.open })
                          }}
                        />

                        <PlanrButton
                          type='secondary'
                          size='small'
                          onClick={() => this.setState({ additionalSumsOpen: true })}
                          style={{ height: "32px", fontSize: "12px", maxWidth: "90px" }}
                        >
                          {addSums.length > 0 ? `(${addSums.length}) Доп выплаты` : `Доп выплаты`}
                        </PlanrButton>
                      </div>
                    )}
                  </div>
                </div>
                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    <Money amount={schedule.actual.increase} />
                  </div>
                  <div className={styles.half}>
                    {!editMode && <Money amount={schedule.plan.increase} />}
                    {editMode && (
                      <Field name={fields.increase}>
                        {({ field, form }: FieldProps) => {
                          return (
                            <MoneyInput
                              className="in-cell planr-tiny-input"
                              id={field.name}
                              small={true}
                              autoComplete="off"
                              data-lpignore="true"
                              value={field.value}
                              onMoneyChange={(money) => {
                                form.setFieldValue(field.name, money.value)
                                form.setFieldTouched(field.name)
                              }}
                            />
                          )
                        }}
                      </Field>
                    )}
                  </div>
                </div>
                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    <Money amount={schedule.actual.bonusWorkedOutGrade} />
                  </div>

                  <div className={styles.half}>
                    {schedule.plan.bonusWorkedOutGrade === 0 ? (
                      <Money amount={schedule.plan.bonusWorkedOutGrade} />
                    ) : (
                      <div className={styles.bonusWorked}>
                        {/* Если есть только workedOut (без grade) */}
                        {schedule.plan.bonusGrade === 0 ? (
                          <PlanrPopover
                            interactionKind="hover"
                            usePortal={true}
                            placement="left"
                            enforceFocus={false}
                            content={
                              <div className={styles.bonusPopup}>
                                <div className={styles.popupRow}>
                                  <span>{`Доплата за стаж (${schedule.plan.bonusWorkedOutPercent}%):`}</span>
                                </div>
                                <div className={styles.popupRow}>
                                  <strong>
                                    <Money amount={schedule.plan.bonusWorkedOut} />
                                  </strong>
                                </div>
                              </div>
                            }
                          >
                            <div className={styles.totalNochildren}>
                              <Money amount={schedule.plan.bonusWorkedOutGrade} className="bp6-tooltip-indicator" />
                            </div>
                          </PlanrPopover>
                        ) : (
                          <>
                            {/* Если есть оба значения - показываем как раньше */}
                            <div className={styles.total}>
                              <Money amount={schedule.plan.bonusWorkedOutGrade} />
                            </div>

                            <PlanrPopover
                              interactionKind="hover"
                              usePortal={true}
                              placement="left"
                              enforceFocus={false}
                              content={
                                <div className={styles.bonusPopup}>
                                  <div className={styles.popupRow}>
                                    <span>{`Доплата за стаж (${schedule.plan.bonusWorkedOutPercent}%):`}</span>
                                  </div>
                                  <div className={styles.popupRow}>
                                    <strong>
                                      <Money amount={schedule.plan.bonusWorkedOut} />
                                    </strong>
                                  </div>
                                </div>
                              }
                            >
                              <div className={styles.workedOut}>
                                <Money amount={schedule.plan.bonusWorkedOut} className="bp6-tooltip-indicator" />
                              </div>
                            </PlanrPopover>

                            {schedule.plan.bonusGrade > 0 && (
                              <PlanrPopover
                                interactionKind="hover"
                                usePortal={true}
                                placement="left"
                                enforceFocus={false}
                                content={
                                  <div className={styles.bonusPopup}>
                                    <div className={styles.popupRow}>
                                      <span>{`Доплата за ранг (${schedule.plan.bonusGradePercent}%):`}</span>
                                    </div>
                                    <div className={styles.popupRow}>
                                      <Money amount={schedule.plan.bonusGrade} />
                                      <strong></strong>
                                    </div>
                                  </div>
                                }
                              >
                                <div className={styles.grade}>
                                  <Money amount={schedule.plan.bonusGrade} className="bp6-tooltip-indicator" />
                                </div>
                              </PlanrPopover>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    <Money amount={schedule.actual.overtime} />
                  </div>
                  <div className={styles.half}>
                    <Money amount={schedule.plan.overtime} />
                  </div>
                </div>
                <div className={`schedule-cell vacation ${styles.cell}`}>
                  {schedule.actual.vacationSalary > 0 && (
                    <PlanrPopover
                      usePortal={true}
                      placement="left"
                      enforceFocus={false}
                      isOpen={undefined}
                      content={
                        <div className={styles.vacations}>
                          {schedule.actual.sortedVacations.map((vacation, index) => (
                            <VacationDetails
                              key={index}
                              vacation={vacation}
                              type="salary"
                              addTransfer={schedule.transfer}
                              removeTransfer={schedule.removeTransfer}
                              readonly={editMode || readonly}
                            />
                          ))}
                        </div>
                      }
                    >
                      <div className={`${styles.actual}`}>
                        <Money amount={schedule.actual.vacationSalary} className="bp6-tooltip-indicator" />
                      </div>
                    </PlanrPopover>
                  )}
                  {schedule.actual.vacationSalary === 0 && (
                    <div className={styles.actual}>
                      <Money amount={schedule.actual.vacationSalary} />
                    </div>
                  )}
                </div>
                <div className={`schedule-cell vacation ${styles.cell}`}>
                  {schedule.actual.vacationBonus > 0 && (
                    <PlanrPopover
                      usePortal={true}
                      placement="left"
                      enforceFocus={false}
                      isOpen={undefined}
                      content={
                        <div className={styles.vacations}>
                          {schedule.actual.sortedVacations.map((vacation, index) => (
                            <VacationDetails
                              key={index}
                              vacation={vacation}
                              type="bonus"
                              addTransfer={schedule.transfer}
                              removeTransfer={schedule.removeTransfer}
                              readonly={editMode || readonly}
                            />
                          ))}
                        </div>
                      }
                    >
                      <div className={`${styles.actual}`}>
                        <Money amount={schedule.actual.vacationBonus} className="bp6-tooltip-indicator" />
                      </div>
                    </PlanrPopover>
                  )}
                  {schedule.actual.vacationBonus === 0 && (
                    <div className={styles.actual}>
                      <Money amount={schedule.actual.vacationBonus} />
                    </div>
                  )}
                </div>
                <div className={`schedule-cell decrease ${styles.cell}`}>
                  {!editMode && <Money amount={schedule.plan.decrease} />}
                  {editMode && (
                    <>
                      <div className={`${styles.half} ${styles.actual}`}>&nbsp;</div>
                      <div className={styles.half}>
                        <Field name={fields.decrease}>
                          {({ field, form }: FieldProps) => {
                            return (
                              <MoneyInput
                                className="in-cell planr-tiny-input"
                                id={field.name}
                                small={true}
                                autoComplete="off"
                                data-lpignore="true"
                                value={field.value}
                                onMoneyChange={(money) => {
                                  form.setFieldValue(field.name, money.value)
                                  form.setFieldTouched(field.name)
                                }}
                              />
                            )
                          }}
                        </Field>
                      </div>
                    </>
                  )}
                </div>
                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    <Money amount={schedule.actual.ndflTaxe} />
                  </div>
                  <div className={styles.half}>
                    <Money amount={schedule.plan.ndflTaxe} />
                  </div>
                </div>
                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    <Money amount={schedule.actual.socialTaxe} />
                  </div>
                  <div className={styles.half}>
                    <Money amount={schedule.plan.socialTaxe} />
                  </div>
                </div>
                {!bonusTaxeHidden && (
                  <div className={`schedule-cell ${styles.cell}`}>
                    <div className={`${styles.half} ${styles.actual}`}>
                      <Money amount={schedule.actual.bonusTaxe} />
                    </div>
                    <div className={styles.half}>
                      <Money amount={schedule.plan.bonusTaxe} />
                    </div>
                  </div>
                )}
                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    <Money amount={schedule.actual.profit} />
                  </div>
                  <div className={styles.half}>
                    <Money amount={schedule.plan.profit} />
                  </div>
                </div>
                <div className={`schedule-cell ${styles.cell}`}>
                  <div className={`${styles.half} ${styles.actual}`}>
                    <Money amount={schedule.actual.companySpending} />
                  </div>
                  <div className={styles.half}>
                    <Money amount={schedule.plan.companySpending} />
                  </div>
                </div>
                <div className={`schedule-cell per-hour ${styles.cell}`}>
                  <div>
                    <Money amount={schedule.plan.companySpendingPerHour} />
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
        {editMode && (
          <AdditionalSumsEditor
            isOpen={this.state.additionalSumsOpen}
            onClose={() => this.setState({ additionalSumsOpen: false })}
            additionalSums={this.formProps?.values.additionalSums || []}
            onSave={(sums) => {
              this.formProps?.setFieldValue(fields.additionalSums, sums)
              this.setState({ additionalSumsOpen: false })
            }}
          />
        )}
      </div>
    )
  }

  /** Правка текущего или будущего месяца протягивается в следующие месяцы, прошлого — только исправляет его */
  get propagatesForward() {
    const { year, month } = this.props.store
    const now = new Date()
    return year * 12 + month >= now.getFullYear() * 12 + now.getMonth() + 1
  }

  clickSubmit = () => this.submit.current && this.submit.current.click();

  onDoubleClick = () => this.schedule && this.props.onDoubleClick(this.schedule.plan);

  onClick = () => setTimeout(() => this.schedule && this.props.onClick(this.schedule.plan), 20);

  onRemove = () => (this.schedule ? this.props.onRemove(this.schedule.plan) : Promise.resolve(false));

  removeLabel = () => "расписание";

  isHighlighted = () => this.props.currentRows.includes(this.props.rowId);

  focusInput = () => {
    setTimeout(() => {
      if (this.div.current) {
        const input = this.div.current.querySelector("input")
        input && input.focus()
      }
    }, MODAL_AWAIT_DELAY / 2)
  };

  cancelEdit = () => {
    const { editMode, onDoubleClick } = this.props
    if (editMode) {
      this.formProps && this.formProps.resetForm()
      onDoubleClick(null)
    }
  };

  onContextMenu = (e: React.MouseEvent) => {
    this.schedule && this.props.onContextMenu(e, this.schedule.plan)
  };
}

export const ScheduleUserRow = observer(Row)

export interface UnitSelector {
  onDoubleClick: (unit: ScheduleUnitType | null) => void
  onClick: (unit: ScheduleUnitType) => void
  currentRows: string[]
}

export interface ScheduleManger {
  onSaved: () => void
  onSave: (id: string, values: any) => Promise<boolean>
  onRemove: (unit: ScheduleUnitType) => Promise<boolean>
  employee: EmployerDictionaryItemType[]
}

interface ScheduleUserRowProps extends UnitSelector, ScheduleManger {
  bonusTaxeHidden?: boolean
  store: ScheduleStoreType
  rowId: string
  editMode?: boolean
  onContextMenu: (e: React.MouseEvent, unit: ScheduleUnitType) => void
  readonly: boolean
}

interface ScheduleUserRowState {
  open: boolean
  additionalSumsOpen: boolean
}

export function getFormValues(store: ScheduleUnitSnapshotType) {
  return {
    [fields.salary]: store.salary || "",
    [fields.bonus]: store.bonus || "",
    [fields.bonusCorrection]: store.bonusCorrection || "",
    [fields.bonusCorrectionComment]: store.bonusCorrectionComment || "",
    [fields.increase]: store.increase || "",
    [fields.decrease]: store.decrease || "",
    [fields.vacationBonus]: store.vacationBonus || "",
    [fields.vacationSalary]: store.vacationSalary || "",
    [fields.employerId]: store.employer ? store.employer.id : "",
    [fields.bonusGradePercent]: store.bonusGradePercent || "",
    [fields.bonusWorkedOutPercent]: store.bonusWorkedOutPercent || "",
    // копия, а не сам узел: Formik держит значения дольше, чем живёт узел строки, и
    // после перезагрузки расписания читал бы удалённый массив (рабочий элемент #27)
    [fields.additionalSums]: (store.additionalSums || []).map(({ guid, name, sum }) => ({ guid, name, sum })),

  }
}
